import { authLogger } from '../../utils/logger';
import type { EmailData, RegistrationNotificationData, EmailContent, IEmailProvider } from './types';

/**
 * Base class for email providers with common functionality
 */
export abstract class BaseEmailProvider implements IEmailProvider {
  protected abstract providerName: string;

  /**
   * Abstract methods that each provider must implement
   */
  abstract sendEmail(data: EmailData): Promise<import('./types').EmailResponse>;
  abstract sendRegistrationNotification(data: RegistrationNotificationData): Promise<import('./types').EmailResponse>;
  abstract validateConfiguration(): boolean;

  /**
   * Get provider name
   */
  getProviderName(): string {
    return this.providerName;
  }

  /**
   * Get FROM email address from environment
   */
  protected getFromEmail(): string {
    return import.meta.env.VITE_FROM_EMAIL || 'noreply@fabrik28.ai';
  }

  /**
   * Get TO email address from environment
   */
  protected getToEmail(): string {
    return import.meta.env.VITE_SEND_TO_EMAIL || 'info@fabrik28.ai';
  }

  /**
   * Generate email content for contact messages
   */
  protected generateContactEmailContent(data: EmailData): EmailContent {
    const subject = data.subject || this.getContactSubject(data.context);
    const fromEmail = this.getFromEmail();
    
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1B365D;">New Contact Message</h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0; color: #2E5984;">Message Details</h3>
          <p><strong>From:</strong> ${data.userName} (${data.userEmail})</p>
          <p><strong>Context:</strong> ${data.context || 'General Contact'}</p>
          <p><strong>Sent:</strong> ${data.timestamp || new Date().toLocaleString()}</p>
        </div>
        
        <div style="background-color: #ffffff; padding: 20px; border: 1px solid #e9ecef; border-radius: 8px;">
          <h3 style="margin-top: 0; color: #2E5984;">Message:</h3>
          <p style="white-space: pre-wrap; line-height: 1.6;">${data.message}</p>
        </div>
        
        <div style="margin-top: 20px; padding: 15px; background-color: #e3f2fd; border-radius: 8px;">
          <p style="margin: 0; font-size: 14px; color: #1565c0;">
            <strong>Reply to:</strong> ${data.userEmail}
          </p>
        </div>
        
        <hr style="margin: 30px 0; border: none; border-top: 1px solid #e9ecef;">
        <p style="font-size: 12px; color: #6c757d; text-align: center;">
          Sent via Fabrik 28 Contact Form
        </p>
      </div>
    `;

    const textContent = `
New Contact Message

From: ${data.userName} (${data.userEmail})
Context: ${data.context || 'General Contact'}
Sent: ${data.timestamp || new Date().toLocaleString()}

Message:
${data.message}

Reply to: ${data.userEmail}

---
Sent via Fabrik 28 Contact Form
    `.trim();

    return {
      subject,
      htmlContent,
      textContent,
      fromEmail,
      toEmail: data.to
    };
  }

  /**
   * Generate email content for registration notifications
   */
  protected generateRegistrationEmailContent(data: RegistrationNotificationData): EmailContent {
    const subject = 'New User Registration - Fabrik 28';
    const fromEmail = this.getFromEmail();
    const toEmail = this.getToEmail();
    
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #1B365D;">🎉 New User Registration</h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0; color: #2E5984;">User Details</h3>
          <p><strong>Name:</strong> ${data.userName}</p>
          <p><strong>Email:</strong> ${data.userEmail}</p>
          <p><strong>Company:</strong> ${data.tenantName || 'Not provided'}</p>
          <p><strong>Subscription:</strong> ${data.subscriptionTier || 'Not selected'}</p>
          <p><strong>Registration Time:</strong> ${new Date(data.timestamp).toLocaleString()}</p>
        </div>
        
        <div style="background-color: #e8f5e8; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0; color: #2e7d32;">
            <strong>Action Required:</strong> Please follow up with the new user to complete their onboarding process.
          </p>
        </div>
        
        <hr style="margin: 30px 0; border: none; border-top: 1px solid #e9ecef;">
        <p style="font-size: 12px; color: #6c757d; text-align: center;">
          Automated notification from Fabrik 28 Registration System
        </p>
      </div>
    `;

    const textContent = `
🎉 New User Registration

User Details:
Name: ${data.userName}
Email: ${data.userEmail}
Company: ${data.tenantName || 'Not provided'}
Subscription: ${data.subscriptionTier || 'Not selected'}
Registration Time: ${new Date(data.timestamp).toLocaleString()}

Action Required: Please follow up with the new user to complete their onboarding process.

---
Automated notification from Fabrik 28 Registration System
    `.trim();

    return {
      subject,
      htmlContent,
      textContent,
      fromEmail,
      toEmail
    };
  }

  /**
   * Get appropriate subject line for contact messages
   */
  private getContactSubject(context?: string): string {
    switch (context) {
      case 'admin-contact':
        return 'Admin Contact Message - Fabrik 28';
      case 'coming-soon':
        return 'Coming Soon Page Message - Fabrik 28';
      case 'test':
        return 'Test Message - Fabrik 28';
      default:
        return 'New Contact Message - Fabrik 28';
    }
  }

  /**
   * Log email attempt
   */
  protected logEmailAttempt(data: EmailData | RegistrationNotificationData, type: 'contact' | 'registration'): void {
    authLogger.info(`📧 Sending ${type} email via ${this.providerName}`, {
      provider: this.providerName,
      to: 'to' in data ? data.to : this.getToEmail(),
      userEmail: data.userEmail,
      userName: data.userName,
      timestamp: data.timestamp
    });
  }

  /**
   * Log email success
   */
  protected logEmailSuccess(messageId?: string, recipient?: string): void {
    authLogger.info(`✅ Email sent successfully via ${this.providerName}`, {
      provider: this.providerName,
      messageId,
      recipient
    });
  }

  /**
   * Log email error
   */
  protected logEmailError(error: any, recipient?: string): void {
    authLogger.error(`❌ Email failed via ${this.providerName}`, {
      provider: this.providerName,
      error: error.message || error,
      recipient
    });
  }
}
